import { createAction, props } from '@ngrx/store';

import {
  UserSession,
  SelectedSubproject,
  ProjectSetup,
  ClientSupport,
  Subproject,
  NamingConvention,
  UserPreferences,
} from '@shared/models';
import {
  ActivityGroup,
  Activity,
  CustomData,
  CustomDataValue,
  ActivityStreamInlineCustomData,
  Location,
  LocationCategory,
  ActivityMobilisation,
  ActivityStream,
  SubprojectConfiguration,
  Weather,
  WeatherValue,
  Consumable,
  Approver,
  Personnel,
  Hse,
  ForecastCategory,
  HiddenSubtab,
  Progress,
  StatusTrackerWithData,
  TableOfColumnWithData,
  ReportConfiguration,
  ProgressWithoutTarget,
  FreeTextField,
} from '@shared/models/project-setup';

export const setUserSession = createAction(
  '[User Details] Set User Session',
  props<{ userDetails: UserSession }>()
);

export const updateUserSession = createAction(
  '[User Details] Update User Session',
  props<{ userDetails: UserSession }>()
);

export const setUserPreferences = createAction(
  '[User Details] Set User Preferences',
  props<{ userPreferences: UserPreferences }>()
);

export const setTermsConditionsAccepted = createAction(
  '[User Details] Set Terms Conditions Accepted',
  props<{ termsConditionsAccepted: boolean }>()
);

export const setSelectedSubproject = createAction(
  '[User Details] Set Selected Subproject',
  props<{ selectedSubproject: SelectedSubproject }>()
);

export const setProjectSetup = createAction(
  '[User Details] Set Project Setup',
  props<{ projectSetup: ProjectSetup }>()
);

export const setStatusTrackers = createAction(
  '[User Details] Set Status Trackers',
  props<{ statusTrackers: StatusTrackerWithData[] }>()
);

export const setLocationCategories = createAction(
  '[User Details] Set Location Categories',
  props<{ locationCategoryList: LocationCategory[] }>()
);

export const setLocations = createAction(
  '[User Details] Set Location',
  props<{ locationList: Location[] }>()
);

export const setActivityGroups = createAction(
  '[User Details] Set Activity Groups',
  props<{ activityGroupList: ActivityGroup[] }>()
);

export const setActivities = createAction(
  '[User Details] Set Activity',
  props<{ activityList: Activity[] }>()
);

export const setActivityStreams = createAction(
  '[User Details] Set Activity Streams',
  props<{ activityStreams: ActivityStream[] }>()
);

export const setHse = createAction(
  '[User Details] Set HSE',
  props<{ hseList: Hse[] }>()
);

export const setForecastCategories = createAction(
  '[User Details] Set Forecast Categories',
  props<{ forecastCategories: ForecastCategory[] }>()
);

export const setConsumables = createAction(
  '[User Details] Set Consumables',
  props<{ consumableList: Consumable[] }>()
);

export const setApprovers = createAction(
  '[User Details] Set Approvers',
  props<{ approverList: Approver[] }>()
);

export const setSelectedClientId = createAction(
  '[User Details] Set Selected Client Id',
  props<{ clientId: string }>()
);

export const setEnableDigitalSignature = createAction(
  '[User Details] Set Enable Digital Signature',
  props<{ updateData: { id: string; enableDigitalSignature: boolean }[] }>()
);

export const setClientSupport = createAction(
  '[User Details] Set Client Support',
  props<{ clientSupport: ClientSupport }>()
);

export const setSubprojects = createAction(
  '[User Details] Set Subprojects',
  props<{ subprojects: Subproject[] }>()
);

export const setSubprojectConfiguration = createAction(
  '[User Details] Set Subproject Configuration',
  props<{ subprojectConfiguration: SubprojectConfiguration }>()
);

export const setCustomData = createAction(
  '[User Details] Set Custom Data',
  props<{ customDataList: CustomData[] }>()
);

export const setActivityMobilisations = createAction(
  '[User Details] Set Activity Mobilisations',
  props<{ activityMobilisationList: ActivityMobilisation[] }>()
);

export const setCustomDataValues = createAction(
  '[User Details] Set Custom Data Values',
  props<{ customDataValueList: CustomDataValue[] }>()
);

export const setActivityStreamInlineCustomData = createAction(
  '[User Details] Set Activity Stream Inline Custom Data',
  props<{
    activityStreamInlineCustomDataList: ActivityStreamInlineCustomData[];
  }>()
);

export const setSubprojectReleased = createAction(
  '[User Details] Set Subproject Released',
  props<{ subprojectId: number }>()
);

export const setWeather = createAction(
  '[User Details] Set Weather',
  props<{ weatherList: Weather[] }>()
);

export const setWeatherValues = createAction(
  '[User Details] Set Weather Values',
  props<{ weatherDataTypeValueList: WeatherValue[] }>()
);

export const setPersonnel = createAction(
  '[User Details] Set Personnel',
  props<{ personnelList: Personnel[] }>()
);

export const setHiddenSubtabs = createAction(
  '[User Details] Set Hidden Subtabs',
  props<{ hiddenSubtabs: HiddenSubtab[] }>()
);

export const setProgress = createAction(
  '[User Details] Set Progress',
  props<{ progress: Progress }>()
);

export const setProgressWithoutTarget = createAction(
  '[User Details] Set Progress Withot Target',
  props<{ progressWithoutTarget: ProgressWithoutTarget }>()
);

export const setReportConfiguration = createAction(
  '[User Details] Set Report Configuration',
  props<{ reportConfiguration: ReportConfiguration }>()
);

export const setNamingConvention = createAction(
  '[User Details] Set Naming Convention',
  props<{ namingConvention: NamingConvention }>()
);

export const setTableOfColumns = createAction(
  '[User Details] Set Table of Columns',
  props<{ tableOfColumns: TableOfColumnWithData[] }>()
);

export const setFreeTextFields = createAction(
  '[User Details] Set Free Text Fields',
  props<{ freeTextFields: FreeTextField[] }>()
);
